import {
  campaignAudienceCommitResponseSchema,
  campaignAudiencePreviewResponseSchema,
  campaignAudienceSummaryResponseSchema,
  campaignAudienceUploadPreviewResponseSchema,
  campaignAudienceUploadResponseSchema,
  campaignListQuerySchema,
  campaignListResponseSchema,
  serializeCampaignListSortParam,
  campaignRecipientListQuerySchema,
  campaignRecipientListResponseSchema,
  configureCampaignAudienceUploadRequestSchema,
  createCampaignRequestSchema,
  createCampaignResponseSchema,
  getCampaignResponseSchema,
  updateCampaignAudienceDefinitionRequestSchema,
  updateCampaignAudienceDefinitionResponseSchema,
  campaignBulkDeleteRequestSchema,
  campaignBulkDeleteResponseSchema,
  campaignMessageCoverageResponseSchema,
  campaignReviewResponseSchema,
  campaignSendTestRequestSchema,
  campaignSendTestResponseSchema,
  campaignSendNowRequestSchema,
  campaignScheduleRequestSchema,
  campaignLaunchResponseSchema,
  campaignCancelScheduleResponseSchema,
  campaignResultsSummarySchema,
  campaignResultsRecipientsQuerySchema,
  campaignResultsRecipientsResponseSchema,
  updateCampaignMessageRequestSchema,
  updateCampaignMessageResponseSchema,
  type CampaignAudienceCommitResponse,
  type CampaignAudiencePreviewResponse,
  type CampaignAudienceSummaryResponse,
  type CampaignAudienceUploadPreviewResponse,
  type CampaignAudienceUploadResponse,
  type CampaignListQuery,
  type CampaignListResponse,
  type CampaignMessageCoverageResponse,
  type CampaignRecipientListQuery,
  type CampaignRecipientListResponse,
  type CampaignReviewResponse,
  type CampaignSendTestRequest,
  type CampaignSendTestResponse,
  type CampaignSendNowRequest,
  type CampaignScheduleRequest,
  type CampaignLaunchResponse,
  type CampaignCancelScheduleResponse,
  type CampaignResultsSummary,
  type CampaignResultsRecipientsQuery,
  type CampaignResultsRecipientsResponse,
  type ConfigureCampaignAudienceUploadRequest,
  type CreateCampaignRequest,
  type CreateCampaignResponse,
  type GetCampaignResponse,
  type UpdateCampaignAudienceDefinitionRequest,
  type UpdateCampaignAudienceDefinitionResponse,
  type UpdateCampaignMessageRequest,
  type UpdateCampaignMessageResponse,
  type CampaignBulkDeleteRequest,
  type CampaignBulkDeleteResponse,
} from "@multi-chat/shared";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPostFormDataWithProgress,
} from "./client.js";

function campaignsPath(organizationId: number): string {
  return `/organizations/${organizationId}/campaigns`;
}

function campaignPath(organizationId: number, campaignId: number): string {
  return `${campaignsPath(organizationId)}/${campaignId}`;
}

export async function listCampaigns(
  organizationId: number,
  query: CampaignListQuery = {},
  signal?: AbortSignal,
): Promise<CampaignListResponse> {
  const parsed = campaignListQuerySchema.parse(query);
  const search = new URLSearchParams();
  if (parsed.page != null) search.set("page", String(parsed.page));
  if (parsed.pageSize != null) search.set("pageSize", String(parsed.pageSize));
  if (parsed.status != null) search.set("status", parsed.status);
  if (parsed.search != null) search.set("search", parsed.search);
  const sort = serializeCampaignListSortParam(parsed.sort ?? []);
  if (sort != null) search.set("sort", sort);
  const qs = search.toString();
  return campaignListResponseSchema.parse(
    await apiGet(`${campaignsPath(organizationId)}${qs.length > 0 ? `?${qs}` : ""}`, signal),
  );
}

export async function createCampaign(
  organizationId: number,
  body: CreateCampaignRequest,
  signal?: AbortSignal,
): Promise<CreateCampaignResponse> {
  const payload = createCampaignRequestSchema.parse(body);
  return createCampaignResponseSchema.parse(
    await apiPost(campaignsPath(organizationId), payload, signal),
  );
}

export async function bulkDeleteCampaigns(
  organizationId: number,
  body: CampaignBulkDeleteRequest,
  signal?: AbortSignal,
): Promise<CampaignBulkDeleteResponse> {
  const payload = campaignBulkDeleteRequestSchema.parse(body);
  return campaignBulkDeleteResponseSchema.parse(
    await apiPost(`${campaignsPath(organizationId)}/bulk-delete`, payload, signal),
  );
}

export async function getCampaign(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<GetCampaignResponse> {
  return getCampaignResponseSchema.parse(
    await apiGet(campaignPath(organizationId, campaignId), signal),
  );
}

export async function updateCampaignAudience(
  organizationId: number,
  campaignId: number,
  body: UpdateCampaignAudienceDefinitionRequest,
  signal?: AbortSignal,
): Promise<UpdateCampaignAudienceDefinitionResponse> {
  const payload = updateCampaignAudienceDefinitionRequestSchema.parse(body);
  return updateCampaignAudienceDefinitionResponseSchema.parse(
    await apiPatch(`${campaignPath(organizationId, campaignId)}/audience`, payload, signal),
  );
}

export async function previewCampaignAudience(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignAudiencePreviewResponse> {
  return campaignAudiencePreviewResponseSchema.parse(
    await apiPost(`${campaignPath(organizationId, campaignId)}/audience/preview`, {}, signal),
  );
}

export async function commitCampaignAudience(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignAudienceCommitResponse> {
  return campaignAudienceCommitResponseSchema.parse(
    await apiPost(`${campaignPath(organizationId, campaignId)}/audience/commit`, {}, signal),
  );
}

export async function getCampaignAudienceSummary(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignAudienceSummaryResponse> {
  return campaignAudienceSummaryResponseSchema.parse(
    await apiGet(`${campaignPath(organizationId, campaignId)}/audience/summary`, signal),
  );
}

export async function listCampaignRecipients(
  organizationId: number,
  campaignId: number,
  query: CampaignRecipientListQuery = {},
  signal?: AbortSignal,
): Promise<CampaignRecipientListResponse> {
  const parsed = campaignRecipientListQuerySchema.parse(query);
  const search = new URLSearchParams();
  if (parsed.cursor != null) search.set("cursor", parsed.cursor);
  if (parsed.limit != null) search.set("limit", String(parsed.limit));
  const qs = search.toString();
  return campaignRecipientListResponseSchema.parse(
    await apiGet(
      `${campaignPath(organizationId, campaignId)}/recipients${qs.length > 0 ? `?${qs}` : ""}`,
      signal,
    ),
  );
}

export async function getCampaignAudienceUpload(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignAudienceUploadResponse> {
  return campaignAudienceUploadResponseSchema.parse(
    await apiGet(`${campaignPath(organizationId, campaignId)}/audience/upload`, signal),
  );
}

export async function uploadCampaignAudienceCsv(
  organizationId: number,
  campaignId: number,
  file: File,
  options: {
    signal?: AbortSignal;
    onProgress?: (percent: number) => void;
    idempotencyKey?: string;
    worksheet?: string;
  } = {},
): Promise<CampaignAudienceUploadResponse> {
  const formData = new FormData();
  if (options.worksheet) formData.append("worksheet", options.worksheet);
  formData.append("fileSizeBytes", String(file.size));
  formData.append("file", file, file.name);
  return campaignAudienceUploadResponseSchema.parse(
    await apiPostFormDataWithProgress(
      `${campaignPath(organizationId, campaignId)}/audience/upload`,
      formData,
      {
        signal: options.signal,
        onProgress: options.onProgress,
        headers: options.idempotencyKey
          ? { "Idempotency-Key": options.idempotencyKey }
          : undefined,
      },
    ),
  );
}

export async function configureCampaignAudienceUpload(
  organizationId: number,
  campaignId: number,
  body: ConfigureCampaignAudienceUploadRequest,
  signal?: AbortSignal,
): Promise<CampaignAudienceUploadResponse> {
  const payload = configureCampaignAudienceUploadRequestSchema.parse(body);
  return campaignAudienceUploadResponseSchema.parse(
    await apiPatch(
      `${campaignPath(organizationId, campaignId)}/audience/upload/config`,
      payload,
      signal,
    ),
  );
}

export async function getCampaignAudienceUploadPreview(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignAudienceUploadPreviewResponse> {
  return campaignAudienceUploadPreviewResponseSchema.parse(
    await apiGet(`${campaignPath(organizationId, campaignId)}/audience/upload/preview`, signal),
  );
}

export async function removeCampaignAudienceUpload(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<{ campaign: CampaignAudienceUploadResponse["campaign"] }> {
  return (await apiDelete(
    `${campaignPath(organizationId, campaignId)}/audience/upload`,
    signal,
  )) as { campaign: CampaignAudienceUploadResponse["campaign"] };
}

export async function updateCampaignMessage(
  organizationId: number,
  campaignId: number,
  body: UpdateCampaignMessageRequest,
  signal?: AbortSignal,
): Promise<UpdateCampaignMessageResponse> {
  const payload = updateCampaignMessageRequestSchema.parse(body);
  return updateCampaignMessageResponseSchema.parse(
    await apiPatch(`${campaignPath(organizationId, campaignId)}/message`, payload, signal),
  );
}

export async function getCampaignMessageCoverage(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignMessageCoverageResponse> {
  return campaignMessageCoverageResponseSchema.parse(
    await apiGet(`${campaignPath(organizationId, campaignId)}/message/coverage`, signal),
  );
}

export async function getCampaignReview(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignReviewResponse> {
  return campaignReviewResponseSchema.parse(
    await apiGet(`${campaignPath(organizationId, campaignId)}/review`, signal),
  );
}

export async function sendCampaignTest(
  organizationId: number,
  campaignId: number,
  body: CampaignSendTestRequest,
  signal?: AbortSignal,
): Promise<CampaignSendTestResponse> {
  const payload = campaignSendTestRequestSchema.parse(body);
  return campaignSendTestResponseSchema.parse(
    await apiPost(`${campaignPath(organizationId, campaignId)}/send-test`, payload, signal),
  );
}

export async function sendCampaignNow(
  organizationId: number,
  campaignId: number,
  body: CampaignSendNowRequest,
  signal?: AbortSignal,
): Promise<CampaignLaunchResponse> {
  const payload = campaignSendNowRequestSchema.parse(body);
  return campaignLaunchResponseSchema.parse(
    await apiPost(`${campaignPath(organizationId, campaignId)}/send`, payload, signal),
  );
}

export async function scheduleCampaign(
  organizationId: number,
  campaignId: number,
  body: CampaignScheduleRequest,
  signal?: AbortSignal,
): Promise<CampaignLaunchResponse> {
  const payload = campaignScheduleRequestSchema.parse(body);
  return campaignLaunchResponseSchema.parse(
    await apiPost(`${campaignPath(organizationId, campaignId)}/schedule`, payload, signal),
  );
}

export async function cancelCampaignSchedule(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignCancelScheduleResponse> {
  return campaignCancelScheduleResponseSchema.parse(
    await apiPost(`${campaignPath(organizationId, campaignId)}/cancel-schedule`, {}, signal),
  );
}

export async function getCampaignResultsSummary(
  organizationId: number,
  campaignId: number,
  signal?: AbortSignal,
): Promise<CampaignResultsSummary> {
  return campaignResultsSummarySchema.parse(
    await apiGet(`${campaignPath(organizationId, campaignId)}/results/summary`, signal),
  );
}

export async function listCampaignResultsRecipients(
  organizationId: number,
  campaignId: number,
  query: CampaignResultsRecipientsQuery = { page: 1, pageSize: 25 },
  signal?: AbortSignal,
): Promise<CampaignResultsRecipientsResponse> {
  const parsed = campaignResultsRecipientsQuerySchema.parse(query);
  const search = new URLSearchParams();
  search.set("page", String(parsed.page));
  search.set("pageSize", String(parsed.pageSize));
  if (parsed.search != null && parsed.search.length > 0) {
    search.set("search", parsed.search);
  }
  if (parsed.resultStatus != null) {
    search.set("resultStatus", parsed.resultStatus);
  }
  return campaignResultsRecipientsResponseSchema.parse(
    await apiGet(
      `${campaignPath(organizationId, campaignId)}/results/recipients?${search.toString()}`,
      signal,
    ),
  );
}
